import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";

export default function Header() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [direction, setDirection] = useState("up");
  const [visible, setVisible] = useState(true);
  const [hasEntered, setHasEntered] = useState(false);
  const prevY = useRef(0);
  const scrollThreshold = 160;

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const dir = currentY > prevY.current ? "down" : "up";
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setDirection(dir);
          setScrollY(currentY);
          prevY.current = currentY;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!hasEntered) return;
    if (scrollY > scrollThreshold && direction === "down") {
      setVisible(false);
    } else if (direction === "up") {
      setVisible(true);
    }
  }, [scrollY, direction, hasEntered]);

  const getTransitionDuration = () => {
    return !hasEntered ? 1.5 : direction === "up" ? 1 : 1.5;
  };

  const navLinks = [
    { name: "HOME", path: "/" },
    { name: "ABOUT", path: "/about" },
    { name: "PROJECTS", path: "/projects" },
    { name: "CONTACT US", path: "/contact" },
  ];

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only absolute top-0 left-0 bg-white text-blue-900 p-2 z-50">
        Skip to main content
      </a>

      <AnimatePresence>
        <motion.header
          role="banner"
          key="header"
          initial={{ opacity: 0, y: -80 }}
          animate={{
            opacity: visible ? 1 : 0,
            y: visible ? 0 : -100,
            height: visible ? "80px" : "0px",
          }}
          transition={{
            duration: getTransitionDuration(),
            ease: "easeInOut",
          }}
          onAnimationComplete={() => setHasEntered(true)}
          className="fixed top-0 left-0 w-full z-50 overflow-hidden bg-white border-b border-gray-200"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex justify-between items-center">
            <h1 className="sr-only">CovePoint Development Group</h1>
            <Link to="/" className="flex items-center" aria-label="CovePoint Homepage">
              <img src="/cp_logo.png" alt="CovePoint" className="h-14 sm:h-18 w-auto" />
            </Link>

            <nav role="navigation" aria-label="Primary site navigation" className="hidden md:flex items-center gap-3 text-sm font-medium text-gray-800">
              {navLinks.map((link, index) => (
                <span key={link.path} className="flex items-center space-x-1">
                  <Link
                    to={link.path}
                    className={`uppercase tracking-wide relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-black after:w-full after:scale-x-0 after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-300 ${
                      location.pathname === link.path ? "after:scale-x-100" : ""
                    }`}
                  >
                    {link.name}
                  </Link>
                  {index < navLinks.length - 1 && (
                    <span className="text-gray-500 text-base leading-none">/</span>
                  )}
                </span>
              ))}
            </nav>

            <button
              className="md:hidden text-gray-800 z-[100]"
              onClick={() => setIsOpen((prev) => !prev)}
              aria-label="Toggle Menu"
            >
              <motion.div
                key={isOpen ? "close" : "menu"}
                initial={{ rotate: isOpen ? -90 : 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: isOpen ? 90 : -90, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {isOpen ? <X size={36} /> : <Menu size={36} />}
              </motion.div>
            </button>
          </div>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                key="mobile-menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed top-0 left-0 w-full h-screen bg-white z-40 flex flex-col"
              >
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
                  <Link to="/" onClick={() => setIsOpen(false)}>
                    <img src="/cp_logo.png" alt="CovePoint" className="h-14" />
                  </Link>
                </div>
                <nav className="flex flex-col px-6 py-4 divide-y divide-gray-200">
                  {navLinks.map(({ name, path }) => (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setIsOpen(false)}
                      className="flex justify-between items-center text-gray-800 text-lg font-medium py-4"
                    >
                      <span>{name}</span>
                      <ChevronRight size={20} className="text-gray-400" />
                    </Link>
                  ))}
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>
      </AnimatePresence>
    </>
  );
}
